import { ApolloClient } from "@apollo/client";
import { IMessageContext } from "@saleor/components/messages";
import { DEMO_MODE } from "@saleor/config";
import {
  GenerateTokenMutation,
  useGenerateTokenMutation,
  useRefreshTokenMutation,
  useUserCompanyQuery,
  useUserDetailsQuery
} from "@saleor/graphql";
import useAppState from "@saleor/hooks/useAppState";
import useLocalStorage from "@saleor/hooks/useLocalStorage";
import { commonMessages } from "@saleor/intl";
import {
  GetExternalAccessTokenData,
  LoginData,
  useAuth,
  useAuthState
} from "@saleor/sdk";
import {
  login as loginWithCredentialsManagementAPI,
  saveCredentials
} from "@saleor/utils/credentialsManagement";
import { Auth } from "aws-amplify";
import { useEffect, useRef, useState } from "react";
import { IntlShape } from "react-intl";

import {
  ExternalLoginInput,
  RequestExternalLoginInput,
  UserContext,
  UserContextError
} from "../types";
import {
  displayDemoMessage,
  getTokens,
  removeTokens,
  setAuthToken
} from "../utils";

export interface UseAuthProviderOpts {
  intl: IntlShape;
  notify: IMessageContext;
  apolloClient: ApolloClient<any>;
}

const URL_MICROSOFT =
  "https://login.microsoftonline.com/common/oauth2/v2.0/logout";

export function useAuthProvider({
  intl,
  notify,
  apolloClient
}: UseAuthProviderOpts): UserContext {
  const { login, getExternalAuthUrl, getExternalAccessToken } = useAuth();
  const { authenticated, authenticating } = useAuthState();
  const [loginSuccess, setLogin] = useState(false);
  const [zone, setZone] = useState(null);
  const [company, setCompany] = useState<string>("");
  const [soldToId, setSoldToId] = useState<string>("");

  const [, dispatchAppState] = useAppState();
  const [requestedExternalPluginId] = useLocalStorage(
    "requestedExternalPluginId",
    null
  );
  const [error, setError] = useState<UserContextError>();
  const permitCredentialsAPI = useRef(true);
  const [generateToken] = useGenerateTokenMutation();
  const [tokenRefresh] = useRefreshTokenMutation();

  useEffect(() => {
    if (authenticating && error) {
      setError(undefined);
    }
  }, [authenticating]);

  const { data: listCompany } = useUserCompanyQuery({
    client: apolloClient,
    skip: loginSuccess ? false : !getTokens().auth,
    fetchPolicy: "cache-and-network"
  });

  useEffect(() => {
    if (listCompany && listCompany?.scgpMe?.soldTos?.length && !company) {
      setCompany(listCompany?.scgpMe?.soldTos[0]?.soldToCode);
      setSoldToId(listCompany?.scgpMe?.soldTos[0]?.id);
    }
  }, [listCompany, company]);

  useEffect(() => {
    if (authenticated) {
      permitCredentialsAPI.current = true;
    }
  }, [authenticated]);

  useEffect(() => {
    if (
      !authenticated &&
      !authenticating &&
      !requestedExternalPluginId &&
      permitCredentialsAPI.current
    ) {
      permitCredentialsAPI.current = false;
      loginWithCredentialsManagementAPI(handleLogin);
    }
  }, [authenticated, authenticating]);

  const userDetails = useUserDetailsQuery({
    client: apolloClient,
    skip: loginSuccess ? false : !getTokens().auth,
    // Don't change this to 'network-only' - update of intl provider's
    // state will cause an error
    fetchPolicy: "cache-and-network"
  });

  const handleLogout = async () => {
    dispatchAppState({
      payload: {
        value: true
      },
      type: "displayLoader"
    });

    const isInternal = localStorage.getItem("amplify-type-auth") === "INTERNAL";
    location.replace(isInternal ? URL_MICROSOFT : "/");
    localStorage.removeItem("amplify-type-auth");

    removeTokens();
    await Auth.signOut();
    await apolloClient.clearStore();
    // Forget last logged in user data.
    // On next login, user details query will be refetched due to cache-and-network fetch policy.
  };

  const handleLogin = async (email: string, password: string) => {
    try {
      const result = await login({
        email,
        password,
        includeDetails: false
      });

      if (result && !result.data.tokenCreate.errors.length) {
        if (DEMO_MODE) {
          displayDemoMessage(intl, notify);
        }
        saveCredentials(result.data.tokenCreate.user, password);
      } else {
        setError("loginError");
      }

      await logoutNonStaffUser(result.data.tokenCreate);

      return result.data.tokenCreate;
    } catch (error) {
      setError("serverError");
    }
  };

  const handleRequestExternalLogin = async (
    pluginId: string,
    input: RequestExternalLoginInput
  ) => {
    const result = await getExternalAuthUrl({
      pluginId,
      input: JSON.stringify(input)
    });

    return result?.data?.externalAuthenticationUrl;
  };

  const handleExternalLogin = async (
    pluginId: string,
    input: ExternalLoginInput
  ) => {
    try {
      const result = await getExternalAccessToken({
        pluginId,
        input: JSON.stringify(input)
      });

      if (result && !result.data?.externalObtainAccessTokens.errors.length) {
        if (DEMO_MODE) {
          displayDemoMessage(intl, notify);
        }
      } else {
        setError("externalLoginError");
      }

      await logoutNonStaffUser(result.data.externalObtainAccessTokens);

      return result?.data?.externalObtainAccessTokens;
    } catch (error) {
      setError("serverError");
    }
  };

  const logoutNonStaffUser = async (
    data: LoginData | GetExternalAccessTokenData
  ) => {
    if (data.user && !data.user.isStaff) {
      notify({
        status: "error",
        text: intl.formatMessage(commonMessages.unauthorizedDashboardAccess),
        title: intl.formatMessage(commonMessages.insufficientPermissions)
      });
      await handleLogout();
    }
  };
  const handleLoginWithToken = async (idToken: string) => {
    if (!idToken) {
      setError("loginError");
      return;
    }
    try {
      const result = await generateToken({ variables: { idToken } });
      const token = result?.data?.generateToken;
      if (!token) {
        setError("loginError");
        return;
      }
      setLogin(true);
      if (token?.errors?.length) {
        const scgLoginError = token.errors.find(
          item => item.field === ("scgLoginError" as any)
        );

        if (scgLoginError) {
          return {
            ...token,
            csrfToken: "FAKE_TOKEN",
            token: "FAKE_TOKEN",
            user: {
              id: "",
              email: "",
              lastName: ""
            }
          } as GenerateTokenMutation["generateToken"];
        }
        return token;
      }
      return token;
    } catch (error) {
      setError("serverError");
    }
  };

  const refreshTokenApp = async (): Promise<void> => {
    try {
      const csrfToken = getTokens().refresh;
      const refreshToken = getTokens().csrf;
      const response = await tokenRefresh({
        variables: { csrfToken, refreshToken }
      });
      if (response) {
        setAuthToken(response.data.tokenRefresh.token, false);
      } else {
        throw new Error("Response not available");
      }
    } catch (error) {
      console.error("An error occurred during refreshTokenApp:", error);
    }
  };

  const refreshToken = async (): Promise<boolean> => {
    const csrfToken = getTokens().refresh;
    const refreshToken = getTokens().csrf;
    try {
      const refreshData = await tokenRefresh({
        variables: { csrfToken, refreshToken }
      });
      if (refreshData?.data?.tokenRefresh?.token) {
        setAuthToken(refreshData.data.tokenRefresh.token, false);
        return true;
      }
    } catch (error) {
      console.error("ERROR in tokenRefresh", error);
    }
    return false;
  };

  return {
    login: handleLogin,
    loginByToken: handleLoginWithToken,
    requestLoginByExternalPlugin: handleRequestExternalLogin,
    loginByExternalPlugin: handleExternalLogin,
    logout: handleLogout,
    authenticating: authenticating && !error,
    authenticated: userDetails?.data?.me?.isStaff,
    user: userDetails.data?.me,
    tokenRefresh: refreshToken,
    tokenRefreshApp: refreshTokenApp,
    error,
    zone,
    setZone,
    company,
    setCompany,
    listCompany: listCompany?.scgpMe?.soldTos || [],
    soldToId,
    setSoldToId
  };
}
